import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, CheckCircle, ArrowRight, DollarSign, 
  Activity, Database, LayoutList, Calculator
} from 'lucide-react';
import { Badge } from '../ui/badge';

const EOBProcessingSlide = () => {
  const [step, setStep] = useState(0);

  // Animation cycle
  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header */}
        <div className="absolute top-8 right-8 lg:right-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm">
                 <img src="/assets/alisha.png" alt="Alisha" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-white leading-none">Alisha</h3>
                <p className="text-xs text-emerald-400 font-bold uppercase tracking-wide">EOB Posting Agent</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-6">
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                    We receive EOBs, calculate adjustments, and <span className="text-emerald-400">update your ledger</span>.
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                   Alisha handles the actual EOB posting process end-to-end. She processes the documents, performs the math for adjustments and patient responsibility, and writes it directly into the Asha PMS ledger.
                </p>

                <div className="space-y-3 pt-4">
                    <ProcessStep 
                        icon={FileText} 
                        title="Receive EOB" 
                        desc="Ingests EOBs from any source instantly."
                        active={step === 0 || step === 3}
                    />
                    <ProcessStep 
                        icon={Calculator} 
                        title="Calculate Adjustments" 
                        desc="Computes write-offs and patient balances."
                        active={step === 1 || step === 3}
                    />
                     <ProcessStep 
                        icon={LayoutList} 
                        title="Update Ledger" 
                        desc="Posts line-item details into Asha PMS."
                        active={step === 2 || step === 3}
                    />
                </div>
            </div>

            {/* Right Side: Flow Visualization */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full h-[500px] relative bg-slate-950/50 rounded-3xl border border-slate-800 p-8 flex items-center justify-between gap-4 overflow-hidden">
                    
                    {/* Background Grid */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:2rem_2rem]" />

                    {/* Step 1: EOB Input */}
                    <motion.div 
                        animate={{ 
                            scale: step === 0 ? 1.05 : 1,
                            opacity: step >= 0 ? 1 : 0.5,
                            borderColor: step === 0 ? 'rgba(16, 185, 129, 0.5)' : 'rgba(30, 41, 59, 1)'
                        }}
                        className="w-1/3 h-64 bg-slate-900 rounded-xl border border-slate-800 flex flex-col items-center justify-center relative z-10 shadow-xl"
                    >
                         <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                            <FileText className="w-8 h-8 text-blue-400" />
                         </div>
                         <h3 className="font-bold text-slate-200">EOB Received</h3>
                         <p className="text-xs text-slate-500 mt-2 text-center px-4">Scanned PDF or ERA 835</p>
                         
                         {step === 0 && (
                             <motion.div 
                                className="absolute inset-0 border-2 border-blue-500 rounded-xl"
                                layoutId="highlight"
                             />
                         )}
                    </motion.div>

                    {/* Arrow 1 */}
                    <div className="flex-shrink-0 text-slate-600">
                        <ArrowRight className={`w-8 h-8 ${step >= 1 ? 'text-emerald-500' : ''} transition-colors duration-500`} />
                    </div>

                    {/* Step 2: Calculation Engine */}
                    <motion.div 
                        animate={{ 
                            scale: step === 1 ? 1.05 : 1,
                            opacity: step >= 1 ? 1 : 0.5,
                            borderColor: step === 1 ? 'rgba(16, 185, 129, 0.5)' : 'rgba(30, 41, 59, 1)'
                        }}
                        className="w-1/3 h-64 bg-slate-900 rounded-xl border border-slate-800 flex flex-col items-center justify-center relative z-10 shadow-xl"
                    >
                         <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                            <Calculator className="w-8 h-8 text-purple-400" />
                         </div>
                         <h3 className="font-bold text-slate-200">Adjustments</h3>
                         <div className="mt-2 space-y-1 text-xs font-mono text-slate-400 bg-slate-950 p-2 rounded w-3/4 opacity-80">
                            <div className="flex justify-between"><span>Billed:</span> <span>$120</span></div>
                            <div className="flex justify-between text-emerald-400"><span>Paid:</span> <span>-$80</span></div>
                            <div className="flex justify-between text-amber-400"><span>Adj:</span> <span>-$40</span></div>
                         </div>

                         {step === 1 && (
                             <motion.div 
                                className="absolute inset-0 border-2 border-purple-500 rounded-xl"
                                layoutId="highlight"
                             />
                         )}
                    </motion.div>

                    {/* Arrow 2 */}
                    <div className="flex-shrink-0 text-slate-600">
                        <ArrowRight className={`w-8 h-8 ${step >= 2 ? 'text-emerald-500' : ''} transition-colors duration-500`} />
                    </div>

                    {/* Step 3: PMS Ledger */}
                    <motion.div 
                         animate={{ 
                            scale: step === 2 ? 1.05 : 1,
                            opacity: step >= 2 ? 1 : 0.5,
                            borderColor: step === 2 ? 'rgba(16, 185, 129, 0.5)' : 'rgba(30, 41, 59, 1)'
                        }}
                        className="w-1/3 h-64 bg-slate-900 rounded-xl border border-slate-800 flex flex-col items-center justify-center relative z-10 shadow-xl"
                    >
                         <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
                            <Database className="w-8 h-8 text-emerald-400" />
                         </div>
                         <h3 className="font-bold text-slate-200">PMS Updated</h3>
                         <div className="mt-3 flex items-center gap-2 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                            <CheckCircle className="w-3 h-3 text-emerald-400" />
                            <span className="text-xs text-emerald-300 font-bold">Posted to Ledger</span>
                         </div>

                         {step === 2 && (
                             <motion.div 
                                className="absolute inset-0 border-2 border-emerald-500 rounded-xl"
                                layoutId="highlight"
                             />
                         )}
                    </motion.div>

                </div>
            </div>
        </div>
    </div>
  );
};

const ProcessStep = ({ icon: Icon, title, desc, active }) => (
    <div className={`flex items-start gap-4 p-3 rounded-xl transition-colors duration-300 ${active ? 'bg-white/10 border border-white/10' : 'opacity-50 border border-transparent'}`}>
        <div className={`p-2 rounded-lg mt-0.5 ${active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-600'}`}>
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <h4 className={`font-bold text-lg ${active ? 'text-white' : 'text-slate-400'}`}>{title}</h4>
            <p className="text-sm text-slate-400 leading-snug">{desc}</p>
        </div>
    </div>
);

export default EOBProcessingSlide;
